// "You're gonna flip." — portrait video + elevated single-quote testimonial
// reel that auto-rotates with a fade transition.
//
// Desktop (≥920px): video on the left, single quote displayed large in the
// right column, crossfading to the next quote every ~7s.
// Mobile: video on top, quote below, same crossfade.
//
// Testimonials live in `src/utils/testimonials.js`. Swap to Firestore in
// Phase B when the keepsake review submission flow is built.

import { useEffect, useRef, useState } from "react";
import PanBar from "../components/PanBar";
import { useTestimonials } from "../../../hooks/useTestimonials";

const VIDEO_EMBED_URL = null;
const FROM_MP4        = true;
const MP4_SRC         = "/website-loop.mp4";
const POSTER_URL      = "/website-loop-poster.jpg";

const QUOTE_DURATION_MS = 8000;

export default function TheFlip() {
  const ref = useRef();
  const videoRef = useRef();
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.25 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      v.muted = true; // required for autoplay on most mobile browsers
      const p = v.play();
      if (p?.catch) p.catch(() => {}); // blocked state handled inside VideoFrame
    } else {
      v.pause();
    }
  }, [inView]);

  const { items: testimonials } = useTestimonials();

  return (
    <section
      id="flip"
      style={{
        background: "radial-gradient(ellipse 130% 110% at 50% 0%, rgba(74,164,225,0.15) 0%, transparent 60%), #0e2e46",
        padding: "5rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ textAlign: "center" }}>
          <PanBar width={72} height={3} margin="0 auto 1.1rem" />
          <h2 style={{
            margin: "0 0 2.5rem",
            fontFamily: "var(--jop-script)",
            fontSize: "clamp(2rem, 5vw, 3.2rem)",
            color: "var(--jop-gold)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}>
            You're gonna flip.
          </h2>
        </div>

        <div className="jop-flip-grid">
          <VideoFrame innerRef={ref} videoRef={videoRef} inView={inView} />
          <TestimonialStage items={testimonials} />
        </div>
      </div>

      <style>{`
        .jop-flip-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
          max-width: 1000px;
          margin: 0 auto;
        }
        @media (min-width: 920px) {
          .jop-flip-grid {
            grid-template-columns: minmax(320px, 380px) 1fr;
            gap: 3rem;
            align-items: center;
          }
        }
      `}</style>
    </section>
  );
}

// ── VIDEO FRAME ──────────────────────────────────────────────────────────────
// Gradient border sits ON TOP of the video via an overlay div that uses
// mask-composite to cut out the inner area, leaving only the 3px border band
// visible. Works around background-clip artifacts at rounded corners.

function VideoFrame({ innerRef, videoRef, inView }) {
  const RADIUS = 18;
  const BORDER = 3;
  const [blocked, setBlocked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Detect autoplay rejection and loading state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => { setPlaying(true); setBlocked(false); setLoaded(true); };
    const onPause = () => setPlaying(false);
    const onReady = () => setLoaded(true);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("canplaythrough", onReady);
    v.addEventListener("playing", onReady);
    if (v.readyState >= 4) setLoaded(true); // already buffered through
    return () => { v.removeEventListener("play", onPlay); v.removeEventListener("pause", onPause); v.removeEventListener("canplaythrough", onReady); v.removeEventListener("playing", onReady); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleTap() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; // ensure muted for autoplay policy
    const p = v.play();
    if (p?.catch) p.catch(() => {});
  }

  return (
    <div
      ref={innerRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "380px",
        margin: "0 auto",
        aspectRatio: "9 / 16",
        borderRadius: `${RADIUS}px`,
        overflow: "hidden",
        background: "#000",
        boxShadow: "0 14px 48px rgba(0,0,0,0.55), 0 0 22px rgba(239,83,126,0.25), 0 0 22px rgba(74,164,225,0.2)",
      }}
    >
      {FROM_MP4 && MP4_SRC ? (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={POSTER_URL}
          onError={() => setBlocked(true)}
          onStalled={() => { /* video stalled — shimmer stays visible */ }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        >
          <source src={MP4_SRC} type="video/mp4" />
        </video>
      ) : VIDEO_EMBED_URL ? (
        inView ? (
          <iframe
            title="The flip"
            src={VIDEO_EMBED_URL}
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        ) : (
          <img src={POSTER_URL} alt="Flip placeholder" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )
      ) : null}

      {/* Branded shimmer — visible while video buffers */}
      {!loaded && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 2,
          background: "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(252,179,31,0.12) 0%, rgba(14,46,70,0.95) 70%, #0e2e46 100%)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem",
        }}>
          <div style={{
            width: "48px", height: "48px", borderRadius: "50%",
            border: "3px solid rgba(252,179,31,0.2)",
            borderTopColor: "var(--jop-gold)",
            animation: "jop-spin 1s linear infinite",
          }} />
          <div style={{
            fontFamily: "var(--jop-script)", fontSize: "1.1rem",
            color: "var(--jop-gold)", opacity: 0.7,
          }}>Loading the magic...</div>
          <style>{`@keyframes jop-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Tap-to-play overlay — shown when autoplay is blocked (Brave, some iOS) */}
      {blocked && !playing && (
        <button
          onClick={handleTap}
          aria-label="Play video"
          style={{
            position: "absolute", inset: 0, zIndex: 3,
            background: "rgba(0,0,0,0.45)",
            border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "rgba(252,179,31,0.9)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          }}>
            <div style={{
              width: 0, height: 0,
              borderTop: "14px solid transparent",
              borderBottom: "14px solid transparent",
              borderLeft: "22px solid #0e2e46",
              marginLeft: "4px",
            }} />
          </div>
        </button>
      )}

      {/* Gradient frame overlay — sits on top of video, corners clean */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: `${RADIUS}px`,
          padding: `${BORDER}px`,
          background: "linear-gradient(140deg, var(--jop-pink) 0%, var(--jop-gold) 50%, var(--jop-highlight-blue) 100%)",
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                  mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
                  maskComposite: "exclude",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

// ── TESTIMONIAL STAGE ────────────────────────────────────────────────────────
// Single quote, elevated. Fades in, stays, fades out, swaps. No container.
// Typography does the lifting.

function TestimonialStage({ items }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef();
  const touchRef = useRef(null);

  // Crossfade to a specific index. Resets the auto-rotate timer so manual
  // navigation gets the full duration before the next auto-advance.
  const goTo = (target) => {
    if (!items || items.length === 0) return;
    const next = ((target % items.length) + items.length) % items.length; // wrap
    if (next === idx) return;
    clearTimeout(timerRef.current);
    setVisible(false);
    setTimeout(() => {
      setIdx(next);
      setVisible(true);
    }, 220); // shorter than auto-rotate (420ms) since user is actively waiting
  };

  // Auto-rotate with a crossfade. On each cycle: fade out → swap quote →
  // fade in.
  useEffect(() => {
    if (!items || items.length < 2) return;
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % items.length);
        setVisible(true);
      }, 420);
    }, QUOTE_DURATION_MS);
    return () => clearTimeout(timerRef.current);
  }, [idx, items]);

  // Touch swipe — horizontal drag ≥40px triggers prev/next.
  const onTouchStart = (e) => {
    const t = e.touches?.[0]; if (!t) return;
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current; touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches?.[0]; if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // not a horizontal swipe
    goTo(idx + (dx < 0 ? 1 : -1));
  };

  if (!items || items.length === 0) return null;
  const t = items[idx];
  const tierColor =
    t.tier === "press"  ? "var(--jop-gold)" :
    t.tier === "booker" ? "var(--jop-pink)" :
                          "var(--jop-highlight-blue)";
  const pancake = t.pancake;
  // Venue caption is derived from the linked pancake's event name (the shift
  // data — the canonical, typo-free source), never from anything the guest
  // typed. Seeds have no pancake, so they fall back to their hand-written
  // context. Real testimonials whose order has aged out of the public gallery
  // simply show no caption.
  const venue = pancake?.eventName || t.context;

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: "relative",
        minHeight: "320px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 0.5rem 2.4rem", // bottom pad reserves space for dots so figcaption can't crash into them
        userSelect: "none",
    }}>
      <figure
        key={t.id}
        style={{
          margin: 0,
          textAlign: "center",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(6px)",
          transition: "opacity 420ms ease, transform 420ms ease",
        }}
      >
        {pancake && (pancake.thumbUrl || pancake.cutoutUrl) && (
          <a
            href={`/gallery/${pancake.id}`}
            aria-label={`View ${pancake.title || "this pancake"} in the gallery`}
            style={{
              display: "inline-block",
              marginBottom: "1.1rem",
              lineHeight: 0,
              borderRadius: "50%",
              transition: "transform 240ms ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.04)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          >
            <img
              src={pancake.thumbUrl || pancake.cutoutUrl}
              alt={pancake.title || "Pancake"}
              loading="lazy"
              style={{
                width: "clamp(110px, 16vw, 150px)",
                height: "clamp(110px, 16vw, 150px)",
                objectFit: "contain",
                background: "transparent",
                filter: `drop-shadow(0 0 14px ${withAlpha(tierColor, 0.55)}) drop-shadow(0 6px 18px rgba(0,0,0,0.45))`,
              }}
            />
          </a>
        )}
        <blockquote style={{
          margin: "0 0 1.4rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(1.35rem, 3.2vw, 2.1rem)",
          lineHeight: 1.3,
          color: "var(--jop-cream)",
          fontWeight: 400,
          letterSpacing: "-0.005em",
          textWrap: "balance",
        }}>
          <span style={{
            color: tierColor,
            fontFamily: "var(--jop-script)",
            fontSize: "1.8em",
            verticalAlign: "-0.35em",
            marginRight: "0.08em",
            lineHeight: 0,
            textShadow: `0 0 18px ${withAlpha(tierColor, 0.5)}`,
          }}>“</span>
          {t.text}
          <span style={{
            color: tierColor,
            fontFamily: "var(--jop-script)",
            fontSize: "1.8em",
            verticalAlign: "-0.35em",
            marginLeft: "0.04em",
            lineHeight: 0,
          }}>”</span>
        </blockquote>
        <figcaption style={{
          fontFamily: "var(--jop-display)",
          fontSize: "0.82rem",
          color: "rgba(255,218,173,0.75)",
          lineHeight: 1.35,
          letterSpacing: "0.02em",
        }}>
          <div style={{ fontWeight: 700, color: "var(--jop-cream)" }}>— {t.authorName}</div>
          {venue && (
            <div style={{
              color: "rgba(255,218,173,0.45)",
              fontSize: "0.72rem",
              marginTop: "0.2rem",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontWeight: 600,
            }}>
              {venue}
            </div>
          )}
        </figcaption>
      </figure>

      {/* Prev/next arrows — desktop-friendly; mobile uses swipe. Kept
          visually subtle so they don't compete with the quote. */}
      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => goTo(idx - 1)}
            aria-label="Previous testimonial"
            style={arrowBtn("left")}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--jop-cream)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,218,173,0.38)"; }}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => goTo(idx + 1)}
            aria-label="Next testimonial"
            style={arrowBtn("right")}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--jop-cream)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,218,173,0.38)"; }}
          >
            ›
          </button>
        </>
      )}

      {/* Dot indicators — now tappable. Jump to any quote. */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: "4px",
      }}>
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            style={{
              background: "none",
              border: "none",
              padding: "8px 3px", // large hit area, small visual
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{
              display: "block",
              width: i === idx ? "14px" : "5px",
              height: "5px",
              borderRadius: "999px",
              background: i === idx ? "var(--jop-gold)" : "rgba(255,218,173,0.3)",
              transition: "background 0.3s ease, width 0.3s ease",
            }} />
          </button>
        ))}
      </div>
    </div>
  );
}

function arrowBtn(side) {
  // Borderless, fill-less — just the glyph. Expanded hit area via padding so
  // touch targets stay healthy without visible chrome.
  return {
    position: "absolute",
    top: "50%",
    [side]: "-0.2rem",
    transform: "translateY(-50%)",
    padding: "10px 12px",
    background: "transparent",
    border: "none",
    color: "rgba(255,218,173,0.38)",
    fontSize: "1.6rem",
    fontFamily: "inherit",
    lineHeight: 1,
    cursor: "pointer",
    transition: "color 0.2s ease, transform 0.2s ease",
  };
}

// Tiny helper — accepts CSS vars for tinting glow. Falls back to hex when
// the var can't be resolved at style-time.
function withAlpha(cssVar, alpha) {
  // CSS vars can't carry alpha directly here; use color-mix for fallback.
  return `color-mix(in srgb, ${cssVar} ${Math.round(alpha * 100)}%, transparent)`;
}
